'use client';

import { useState } from 'react';
import { PartyPopper, X, ThumbsUp, ThumbsDown, Send, ArrowRight } from 'lucide-react';
import { useGuidedTour } from './GuidedTourProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface GuidedTourCelebrationProps {
  onDismiss?: () => void;
}

export function GuidedTourCelebration({ onDismiss }: GuidedTourCelebrationProps) {
  const { isOpen, engine, selectedFlow, closeTour, moduleKey } = useGuidedTour();
  const [showFeedback, setShowFeedback] = useState(false);
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  if (
    !isOpen ||
    engine.viewMode !== 'runner' ||
    !engine.currentStep ||
    !engine.isLastStep ||
    engine.isMinimized
  ) {
    return null;
  }

  const handleSubmitFeedback = () => {
    if (helpful === null) return;

    // Track feedback analytics
    fetch('/api/v1/training/tour-analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'feedback',
        tour_key: moduleKey,
        flow_id: engine.selectedFlowId,
        feedback_rating: helpful ? 5 : 1,
        feedback_helpful: helpful,
        feedback_comment: comment.trim() || undefined,
      }),
    }).catch(() => {});

    setFeedbackSubmitted(true);
  };

  const handleDismissAll = () => {
    setShowFeedback(false);
    setHelpful(null);
    setComment('');
    setFeedbackSubmitted(false);
    closeTour();
  };

  return (
    <div className="pointer-events-auto fixed bottom-4 right-4 z-[70] w-[min(92vw,24rem)]">
      <Card className="border-emerald-200/80 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-2xl backdrop-blur-md dark:border-emerald-800/60 dark:from-emerald-900/30 dark:to-teal-900/30">
        <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-emerald-900 dark:text-emerald-100">
            <PartyPopper className="h-5 w-5 text-emerald-600" />
            Flow complete!
          </CardTitle>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleDismissAll}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {!showFeedback && !feedbackSubmitted && (
            <>
              <p className="text-sm text-emerald-800 dark:text-emerald-200">
                You have completed <strong>{selectedFlow?.label ?? 'the tour'}</strong>. Nice work!
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowFeedback(true)}
                >
                  Rate this tour
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    engine.returnToIndex();
                    onDismiss?.();
                  }}
                >
                  Back to index
                </Button>
                <Button size="sm" className="flex-1" onClick={handleDismissAll}>
                  Done
                </Button>
              </div>
            </>
          )}

          {showFeedback && !feedbackSubmitted && (
            <>
              <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                Was this tour helpful?
              </p>
              <div className="flex gap-2">
                <Button
                  variant={helpful === true ? 'default' : 'outline'}
                  size="sm"
                  className={cn('flex-1 gap-2', helpful === true && 'bg-emerald-600 hover:bg-emerald-700')}
                  onClick={() => setHelpful(true)}
                >
                  <ThumbsUp className="h-4 w-4" />
                  Helpful
                </Button>
                <Button
                  variant={helpful === false ? 'default' : 'outline'}
                  size="sm"
                  className={cn('flex-1 gap-2', helpful === false && 'bg-rose-600 hover:bg-rose-700')}
                  onClick={() => setHelpful(false)}
                >
                  <ThumbsDown className="h-4 w-4" />
                  Not helpful
                </Button>
              </div>
              <Textarea
                placeholder="Any comments? (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="min-h-[70px] text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowFeedback(false)}
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  className="flex-1 gap-2"
                  disabled={helpful === null}
                  onClick={handleSubmitFeedback}
                >
                  <Send className="h-4 w-4" />
                  Submit
                </Button>
              </div>
            </>
          )}

          {feedbackSubmitted && (
            <>
              <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                Thanks for your feedback!
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    engine.returnToIndex();
                    onDismiss?.();
                  }}
                >
                  Back to index
                </Button>
                <Button size="sm" className="flex-1" onClick={handleDismissAll}>
                  Done
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
